import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, AlertTriangle, Lock } from "lucide-react";
import "@/styles/brand-a.css";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";
import { useClinicalTool } from "@/lib/clinical-tools";
import { SEO } from "@/lib/seo";
import { Gad2Form } from "@/pages/tools/Gad2Form";

/**
 * Tool detail router.
 *
 * Maps /tools/:slug to the correct interactive implementation. GAD-2 is the
 * reference live form; the other 11 tools render a "preview" stub that
 * still logs a usage event and links to the external reference.
 *
 * Why keep this as one route rather than per-tool routes: each tool is
 * independent but shares the same chrome (header, reference citation,
 * "last reviewed" stamp, telemetry opt-in). Centralising the shell keeps
 * the 12 implementations under 30 lines each.
 */

function ToolShell({ title, subtitle, reference, referenceUrl, lastReviewedAt, children }: {
  title: string;
  subtitle?: string;
  reference?: string | null;
  referenceUrl?: string | null;
  lastReviewedAt?: string | null;
  children: React.ReactNode;
}) {
  const { city } = useCity();
  return (
    <div className="brand-a" style={{ minHeight: "100dvh" }}>
      <nav className="ba-nav" style={{ top: 0 }}>
        <Link to="/" className="ba-wordmark">
          <svg viewBox="0 0 28 28" width="22" height="22" aria-hidden="true">
            <rect x="2" y="2" width="24" height="24" rx="6" fill="#1F8A8A" />
            <path d="M7 15 L11 15 L12.5 11 L14.5 19 L16 15 L21 15" stroke="#F5F1EB" strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="21" cy="15" r="1.3" fill="#F27E62" />
          </svg>
          Pulseboard
          <span className="ba-wordmark__city">{city.name}</span>
        </Link>
        <ul className="ba-nav__links">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/directory">Directory</Link></li>
          <li><Link to="/board/resources">Tools</Link></li>
        </ul>
      </nav>

      <div className="mx-auto max-w-3xl px-4 pt-12 pb-24 md:px-8 md:pt-20">
        <Link
          to="/board/resources"
          className="inline-flex items-center gap-2 text-sm font-medium text-pb-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to tools
        </Link>

        <h1 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-tight text-pb-ink md:text-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="pb-prose mt-3 text-pb-ink-2">{subtitle}</p>
        )}
        {(reference || lastReviewedAt) && (
          <p className="mt-4 text-xs font-medium uppercase tracking-[0.15em] text-pb-ink-3">
            {reference && (
              <>
                Reference: {referenceUrl ? (
                  <a
                    href={referenceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pb-primary hover:underline inline-flex items-center gap-1"
                  >
                    {reference} <ExternalLink className="h-3 w-3" aria-hidden />
                  </a>
                ) : reference}
              </>
            )}
            {lastReviewedAt && (
              <span className="ml-3 text-pb-ink-3/80">
                · Reviewed {new Date(lastReviewedAt).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}
              </span>
            )}
          </p>
        )}

        <div className="mt-10">{children}</div>
      </div>
    </div>
  );
}

function ComingSoon({ slug, referenceUrl }: {
  slug: string;
  referenceUrl?: string | null;
}) {
  return (
    <div
      className="rounded-lg border border-pb-border bg-pb-surface p-8"
      style={{ borderLeft: "3px solid var(--color-pb-primary)" }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-pb-primary mt-0.5" aria-hidden />
        <div>
          <h2 className="font-display text-lg font-semibold text-pb-ink">
            Interactive version in development
          </h2>
          <p className="mt-2 text-sm text-pb-ink-2">
            This tool's logic is specified and the catalogue card is live, but the
            interactive form hasn't shipped yet. GAD-2 is the live reference
            implementation — the other 11 tools will follow the same pattern.
          </p>
          {referenceUrl && (
            <p className="mt-4 text-sm">
              <a
                href={referenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-pb-primary font-medium hover:underline"
              >
                Open the source guideline <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </p>
          )}
          <p className="mt-6 text-[11px] font-mono uppercase tracking-wider text-pb-ink-3">
            tool · {slug}
          </p>
        </div>
      </div>
    </div>
  );
}

function SignInPrompt({ slug }: { slug: string }) {
  return (
    <div
      className="rounded-lg border border-pb-border bg-pb-surface p-8"
      style={{ borderLeft: "3px solid var(--color-pb-primary)" }}
    >
      <div className="flex items-start gap-3">
        <Lock className="mt-0.5 h-5 w-5 text-pb-primary" aria-hidden />
        <div>
          <h2 className="font-display text-lg font-semibold text-pb-ink">
            Sign in to use this calculator
          </h2>
          <p className="mt-2 text-sm text-pb-ink-2">
            The catalogue is open to everyone, but interactive calculators are
            available to signed-in GPs and specialists so usage is auditable
            and results can feed your morning brief.
          </p>
          <div className="mt-5">
            <Link to="/auth" className="ba-btn ba-btn--primary ba-btn--sm">
              <Lock className="h-3.5 w-3.5" aria-hidden />
              Sign in
            </Link>
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-wider text-pb-ink-3">
            tool · {slug}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ToolPage() {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth();
  const { data: tool, isLoading, error } = useClinicalTool(slug);

  if (isLoading) {
    return (
      <ToolShell title="Loading tool…">
        <div className="h-48 animate-pulse rounded-md border border-pb-border bg-pb-surface" />
      </ToolShell>
    );
  }

  if (error || !tool) {
    return (
      <ToolShell title="Tool not found">
        <p className="pb-prose text-pb-ink-2">
          This tool isn't in the catalogue. It may have been renamed or retired.
        </p>
      </ToolShell>
    );
  }

  const body = tool.implementation_key !== "gad2-v1"
    ? <ComingSoon slug={tool.slug} referenceUrl={tool.reference_url} />
    : isAuthenticated
      ? <Gad2Form toolSlug={tool.slug} />
      : <SignInPrompt slug={tool.slug} />;

  return (
    <>
      <SEO
        title={`${tool.name} · PulseBoard clinical toolbox`}
        description={tool.description}
        path={`/tools/${tool.slug}`}
      />
      <ToolShell
        title={tool.name}
        subtitle={tool.description}
        reference={tool.reference}
        referenceUrl={tool.reference_url}
        lastReviewedAt={tool.last_reviewed_at}
      >
        {body}
      </ToolShell>
    </>
  );
}
